// De weergave-opties die voor de hele widget gelden (tabblad Weergave in het dashboard, golf 1A, Martijn 10-10-2026):
//
//  1. De taal. Een vaste taal uit de weergave, of automatisch de taal van de browser als de zaak die heeft. Een keer, zodra
//     de instellingen binnen zijn; kiest de gast daarna zelf een andere taal, dan blijft die staan.
//  2. Het live voorbeeld in het dashboard (?weergavevoorbeeld=1): het dashboard stuurt de opmaak en de weergave die je aan
//     het invullen bent, en de widget toont ze meteen, zonder op te slaan. Alleen berichten van de pagina eromheen.
//     Golf 2D (11-10-2026): ook de namen van de gastgroepen (velden, scherm Gastgroepen) en welk vak open moet (paneel).
import { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { updateDefaultLanguage as horecaTaal } from "../store/slice/Addguest.slice";
import { updateDefaultLanguage as activiteitTaal } from "../store/slice/Activity.slice";
import { updateDefaultLanguage as ticketTaal } from "../store/slice/Ticketing.slice";
import {
  updateCss,
  updateDefaultLanguage as salonTaal,
  updateWidgetConfig,
  zetVoorbeeldPaneel,
} from "../store/slice/widgetSetting.slice";
import { isVoorbeeld, isVoorbeeldBericht, kiesTaal, voegCssSamen, voegInstellingenSamen, voegWeergaveSamen } from "../lib/weergave";
import { voegVeldenSamen } from "../lib/gastgroepen";
import { STANDAARDTEKSTEN_TYPE, standaardteksten } from "../lib/standaardteksten";
import { huidigeTaal } from "../lib/woorden";

// De woorden per module (voor de standaardteksten in het dashboard, Martijn 11-10-2026).
const WOORDEN = {
  horeca: (s) => s?.addGuest?.languageWords,
  activity: (s) => s?.Activity?.languageWords,
  ticketing: (s) => s?.Ticketing?.languageWords,
  salon: (s) => s?.salon?.languageWords,
};

// Per module: waar de taal staat en hoe je hem zet. De webwinkel doet zijn eigen opties (bouwer C).
const MODULES = {
  horeca: { lees: (s) => s?.addGuest?.defaultLanguage, zet: horecaTaal },
  activity: { lees: (s) => s?.Activity?.defaultLanguage, zet: activiteitTaal },
  ticketing: { lees: (s) => s?.Ticketing?.defaultLanguage, zet: ticketTaal },
  salon: { lees: (s) => s?.widgetSettings?.defaultLanguage, zet: salonTaal },
};

export default function WeergaveRegelaar() {
  const dispatch = useDispatch();
  const winkel = useStore();
  const { widgetConfig, languageData, resType, widgetSetupId } = useSelector((s) => s?.widgetSettings) || {};
  const gedaan = useRef(null);

  // 1. De taal, een keer per widget.
  useEffect(() => {
    const weergave = widgetConfig?.weergave;
    const module = MODULES[String(resType || "").toLowerCase()];
    if (!weergave || !module || gedaan.current === widgetSetupId) return;
    gedaan.current = widgetSetupId;

    const nu = module.lees(winkel.getState());
    if (!nu) return;
    const navigatorTaal = typeof navigator !== "undefined" ? navigator.language : null;
    const gewenst = kiesTaal(weergave, languageData, nu, navigatorTaal);
    if (gewenst && String(gewenst).toUpperCase() !== String(nu).toUpperCase()) {
      dispatch(module.zet(gewenst));
    }
  }, [widgetConfig, languageData, resType, widgetSetupId, dispatch, winkel]);

  // 2. Het live voorbeeld. "voorbeeld-klaar" gaat naar het dashboard zodra de instellingen van de server binnen zijn (ook na
  //    een taalwissel, die ze opnieuw ophaalt); het dashboard stuurt dan wat er nu is ingevuld. Niet na een eigen aanpassing.
  const voorbeeld = typeof window !== "undefined" && window.parent !== window && isVoorbeeld(window.location.search);
  const eigen = useRef(null);

  useEffect(() => {
    if (!voorbeeld) return undefined;

    const luister = (gebeurtenis) => {
      if (!isVoorbeeldBericht(gebeurtenis, window.parent)) return;
      const { css, weergave, velden, paneel, instellingen } = gebeurtenis.data;
      const stand = winkel.getState()?.widgetSettings;
      if (css && typeof css === "object") dispatch(updateCss(voegCssSamen(stand?.css, css)));
      // Golf 2D: de namen van de gastgroepen uit het scherm Gastgroepen, en de gastgroepen/tijden van deze widget (weergave).
      const vakken = velden && typeof velden === "object";
      // Widget bewerken, stap Afbeelding (11-10-2026): afbeelding, plek van het formulier, hoogte en grenzen voor de gasten.
      const losse = instellingen && typeof instellingen === "object";
      if ((weergave && typeof weergave === "object") || vakken || losse) {
        let nieuw = vakken ? voegVeldenSamen(stand?.widgetConfig, velden) : stand?.widgetConfig;
        if (weergave && typeof weergave === "object") nieuw = voegWeergaveSamen(nieuw, weergave);
        if (losse) nieuw = voegInstellingenSamen(nieuw, instellingen);
        dispatch(updateWidgetConfig(nieuw));
        // Wat de store ervan maakte (hij filtert de gastgroepen): dat is de eigen aanpassing, geen nieuwe stand van de server.
        eigen.current = winkel.getState()?.widgetSettings?.widgetConfig;
      }
      // Het vak waar het dashboard over gaat open zetten (gasten, tijden), of dicht.
      dispatch(zetVoorbeeldPaneel(paneel ?? null));
    };

    window.addEventListener("message", luister);
    return () => window.removeEventListener("message", luister);
  }, [voorbeeld, dispatch, winkel]);

  // 3. De standaardteksten van de widget naar het dashboard (tabblad Weergave > Teksten): een leeg veld toont daar wat de gast
  //    nu ziet, in de taal van het voorbeeld.
  const module = String(resType || "").toLowerCase();
  const woorden = useSelector((s) => (WOORDEN[module] ? WOORDEN[module](s) : null));
  const { css, buttonText } = useSelector((s) => s?.widgetSettings) || {};
  const gemeld = useRef(null);

  useEffect(() => {
    if (!voorbeeld || !WOORDEN[module]) return;
    const kopUitOpmaak = Array.isArray(css) ? css.find((g) => g?.cssTabFieldsGroupName === "Overall")?.fields?.MainText_fieldText : null;
    const teksten = standaardteksten({ module, languageWords: woorden, kopUitOpmaak, zaakknop: buttonText });
    const taal = huidigeTaal().toUpperCase();
    const sleutel = taal + "|" + JSON.stringify(teksten);
    if (gemeld.current === sleutel) return;
    gemeld.current = sleutel;
    try {
      window.parent.postMessage({ bron: "jimani-widget", soort: STANDAARDTEKSTEN_TYPE, taal, teksten }, "*");
    } catch { /* geen ouder */ }
  }, [voorbeeld, module, woorden, css, buttonText]);

  useEffect(() => {
    if (!voorbeeld || !widgetConfig || widgetConfig === eigen.current) return;
    try {
      window.parent.postMessage({ bron: "jimani-widget", soort: "voorbeeld-klaar" }, "*");
    } catch { /* geen ouder */ }
  }, [voorbeeld, widgetConfig]);

  return null;
}
